import { useCallback, useEffect, useRef, useState } from "react";

const STORE_KEY = "userLastAction";

const USER_EVENTS = [
  "click",
  "mousedown",
  "mouseup",
  "mousewheel",
  "touchmove",
  "touchstart",
  "touchend",
  "keypress",
  "resize",
  "scroll"
];

interface UseIdleTimerProps {
  idleTime: number;
  warningTime: number;
  onIdle: () => void;
  onWarning: () => void;
  onActive: () => void;
  modalOpen: boolean;
}

export const useIdleTimer = ({
  idleTime,
  warningTime,
  onIdle,
  onWarning,
  onActive,
  modalOpen
}: UseIdleTimerProps) => {
  const [isIdle, setIsIdle] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(warningTime * 60);

  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActionRef = useRef<number>(Date.now());
  const showWarningRef = useRef(showWarning);
  const modalOpenRef = useRef(modalOpen);

  // Store callbacks in refs to avoid re-running effects when they change
  const onIdleRef = useRef(onIdle);
  const onWarningRef = useRef(onWarning);
  const onActiveRef = useRef(onActive);

  // Update refs when callbacks change
  useEffect(() => {
    onIdleRef.current = onIdle;
    onWarningRef.current = onWarning;
    onActiveRef.current = onActive;
  }, [onIdle, onWarning, onActive]);

  // Update showWarning ref when state changes
  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  // Update modalOpen ref when prop changes
  useEffect(() => {
    modalOpenRef.current = modalOpen;
  }, [modalOpen]);

  // Store last action in localStorage
  const updateLastAction = useCallback(() => {
    const now = Date.now();
    lastActionRef.current = now;
    localStorage.setItem(STORE_KEY, now.toString());
  }, []);

  // Reset all timers
  const resetTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    setIsIdle(false);
    setShowWarning(false);
    setTimeLeft(warningTime * 60);
    updateLastAction();
  }, [updateLastAction, warningTime]);

  // Start warning countdown
  const startWarningCountdown = useCallback(() => {
    setShowWarning(true);
    const initialSeconds = warningTime * 60;
    setTimeLeft(initialSeconds);

    // Notify parent
    setTimeout(() => onWarningRef.current(), 0);

    // Clear any existing interval
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = setInterval(() => {
      setTimeLeft(prevTime => {
        const newTime = prevTime - 1;

        if (newTime <= 0) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          setTimeout(() => {
            setIsIdle(true);
            onIdleRef.current();
          }, 0);
          return 0;
        }

        return newTime;
      });
    }, 1000);
  }, [warningTime]);

  // Handle user activity
  const handleUserActivity = useCallback(() => {
    // Ignore user activity when modal is open to allow button clicks
    if (modalOpenRef.current) {
      return;
    }

    if (showWarningRef.current) {
      // User became active during warning (but modal not yet open)
      resetTimers();
      onActiveRef.current();
      // Restart idle timer
      idleTimerRef.current = setTimeout(() => {
        startWarningCountdown();
      }, idleTime * 60 * 1000);
    } else {
      // Normal activity - just update last action
      updateLastAction();
    }
  }, [resetTimers, updateLastAction, idleTime, startWarningCountdown]);

  // Start idle timer
  const startIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = setTimeout(() => {
      startWarningCountdown();
    }, idleTime * 60 * 1000);
  }, [idleTime, startWarningCountdown]);

  // Check for automatic logout on visibility change
  const checkAutoLogout = useCallback(() => {
    const lastAction = parseInt(localStorage.getItem(STORE_KEY) || "0", 10);
    const totalTimeout = (idleTime + warningTime) * 60 * 1000;
    const timeElapsed = Date.now() - lastAction;

    if (timeElapsed >= totalTimeout) {
      setIsIdle(true);
      onIdleRef.current();
      return true;
    }
    return false;
  }, [idleTime, warningTime]);

  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkAutoLogout();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkAutoLogout]);

  // Setup event listeners
  useEffect(() => {
    USER_EVENTS.forEach(event => {
      window.addEventListener(event, handleUserActivity, true);
    });

    startIdleTimer();

    return () => {
      USER_EVENTS.forEach(event => {
        window.removeEventListener(event, handleUserActivity, true);
      });
      resetTimers();
      localStorage.removeItem(STORE_KEY);
    };
  }, [handleUserActivity, startIdleTimer, resetTimers]);

  // Format time for display
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return {
    isIdle,
    showWarning,
    timeLeft,
    formattedTime: formatTime(timeLeft),
    resetTimers,
    continueSession: () => {
      resetTimers();
      startIdleTimer();
    }
  };
};
